"use client";

import { useState } from "react";
import { Input, Button } from "@heroui/react";
import { IAirdropTaskRender } from "@/types/task-renderer.types";
import { getAirdropArgumentValue } from "@/lib/challenges";
import { ChallengeCampaignEventState } from "@/types/challenges.types";
import { QuestTaskButton } from "../QuestTaskButton";
import { Twitter } from "lucide-react";

interface MediaRendererProps extends IAirdropTaskRender {
  placeholder?: string;
}

export const MediaRenderer = ({ task, completeTaskMutation, campaign, placeholder = "https://x.com/status/..." }: MediaRendererProps) => {
  const [url, setUrl] = useState(task.extraArguments?.[0] || undefined);
  const args = campaign.tasks?.find((campaignTask) => campaignTask.id === task.taskGuid)?.arguments ?? [];

  const type = getAirdropArgumentValue(args, "type") ?? "";
  const tweetText = getAirdropArgumentValue(args, "tweetText") ?? "";

  const isDisabled =
    task.status === ChallengeCampaignEventState.PENDING || task.status === ChallengeCampaignEventState.SUCCESSFUL || !!task.cooldown || completeTaskMutation.isPending;

  const label = type === "twitter" ? "X Link" : "";

  return (
    <>
      {/* Input and Claim Button - Mobile: stacked, Desktop: same row */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="flex-1">
          <Input
            disabled={task.status !== ChallengeCampaignEventState.ERROR || isDisabled}
            type="text"
            value={url}
            onValueChange={setUrl}
            placeholder={placeholder}
            label={label}
            variant="bordered"
            classNames={{
              inputWrapper: "border-green-500/30 bg-green-900/20 hover:border-green-500/50 focus-within:border-green-500 transition-colors duration-200 rounded-2xl",
              label: "text-gray-300 font-medium",
              input: "text-white placeholder:text-gray-400 px-3 py-2",
            }}
          />
        </div>
        <div className="flex-shrink-0">
          <QuestTaskButton
            buttonText="Claim"
            taskMutation={completeTaskMutation}
            className="mt-0 w-full sm:w-auto"
            task={task}
            onClick={() => {
              completeTaskMutation.mutateAsync({
                campaignId: campaign.id,
                groupId: campaign.campaignGroup,
                taskName: task.taskName,
                extraArguments: [url ?? ""],
                taskGuid: task.taskGuid,
              });
            }}
          />
        </div>
      </div>

      {/* Tweet button below if available */}
      {tweetText && (
        <div className="mt-4">
          <Button
            size="lg"
            variant="bordered"
            className="rounded-lg border-gray-600/30 text-green-500 hover:bg-green-500/5 transition-all duration-200 font-medium"
            startContent={<Twitter className="w-4 h-4" />}
            onPress={() => {
              const formattedText = tweetText.replace(/\\n/g, "\n");
              const encodedText = encodeURIComponent(formattedText);
              window.open(`https://twitter.com/intent/tweet?text=${encodedText}`, "_blank");
            }}
          >
            Tweet
          </Button>
        </div>
      )}
    </>
  );
};
